/**
 * MegaphoneEngine — the sound of /megaphone: megaphone, walkie-talkie, phone,
 * station PA, cabin PA, old radio. ONE graph for the three places the tool
 * makes sound:
 *
 *   · the preview of a file (AudioContext),
 *   · the live microphone (AudioContext, MediaStreamSource in),
 *   · the exported file (OfflineAudioContext, rendered in windows).
 *
 * WHY ONE GRAPH. Until 2026-10-08 the preview was a Web Audio graph and the
 * export an FFmpeg filter chain written to resemble it: WaveShaper against
 * alimiter, DynamicsCompressor against acompressor, a comb «horn» only in the
 * preview, peaking Q 2.0/2.5 against octave widths, an echo loop against
 * aecho. Two halves of the same tool disagreed about their own sound (the
 * linear band was brought within ±0.04 dB on 2026-10-07; the rest could not
 * be, by construction). Here the export IS the preview, rendered offline:
 * the same nodes, the same parameter mapping, the same deterministic noise.
 *
 * WINDOWS. An OfflineAudioContext holds its whole output, so a long file is
 * rendered in windows of WINDOW_S with a preroll in front of each: every
 * stateful stage (compressor envelopes, the echo and horn loops, the hall
 * convolver) has run for `prerollSec()` before the first sample that is kept,
 * which is longer than the slowest of them takes to forget the past (the echo
 * loop decays 80 dB within it, the convolver is FIR). Every source starts on an
 * integer frame, so windows meet sample-exact.
 *
 * Q UNITS. For lowpass/highpass BiquadFilterNodes Web Audio reads Q in
 * DECIBELS — Butterworth is −3.0103, not 0.707 (docs/webaudio-biquad-q.md,
 * WebAudioLowpassQIsInDecibelsTest). Peaking filters take the ordinary linear Q.
 *
 * LOOPS ARE STABLE BY CONSTRUCTION. Both feedback loops (horn comb, echo) are
 * delay → Butterworth lowpass (|H| ≤ 1 everywhere) → gain < 1. The loop the
 * old preview had (delay → gain 0.35 → two peaking filters of up to +15 and
 * +10 dB → delay) had a loop gain above 6 at the resonance and only the output
 * ceiling kept it from running away; tests/js/megaphone-engine.test.mjs holds
 * every preset's loop gains below 1.
 *
 * Exposed as window.MegaphoneEngine. Pure helpers (presets, plan, timeline,
 * curves, responseDb, speechLevelDb) need no audio context and are what the
 * node tests read.
 */
(function () {
'use strict';

/* ── units ──────────────────────────────────────────────────────────────── */
var BUTTER_Q_DB = 20 * Math.log10(Math.SQRT1_2);           // −3.0103 (lowpass/highpass)
// 4th-order Butterworth as two biquads: Q 0.5412 and 1.3066, in Web Audio's dB.
var BUTTER4_Q_DB = [20 * Math.log10(0.5411961), 20 * Math.log10(1.3065630)];
// 8th-order Butterworth as four biquads (Q = 1 / (2·cos((2k−1)·π/16))).
var BUTTER8_Q_DB = [0.5097956, 0.6013449, 0.8999762, 2.5629154].map(function (q) { return 20 * Math.log10(q); });
function db2g(db) { return Math.pow(10, db / 20); }
function g2db(g) { return 20 * Math.log10(Math.max(g, 1e-12)); }
function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
function num(v, d) { v = +v; return isFinite(v) ? v : d; }

var SHAPER_DOMAIN = 8;      // ±18 dB of drive headroom inside the shaper table
var CEIL_DOMAIN = 4;        // ±12 dB through the output ceiling
var CEIL_LIMIT = 0.92;      // the file's peaks never pass this (−0.72 dBFS)
var WINDOW_S = 30;          // offline render window
var SPEECH_TARGET_DB = -18; // where the active-speech level of a file is put before the chain
var LIVE_TRIM_DB = 14;      // a microphone's speech sits ~14 dB below a mastered file's

/* ── presets ─────────────────────────────────────────────────────────────
   `band` is the preset's natural passband (the «Полоса» control at 50 keeps
   it, 0 halves its width in octaves, 100 widens it ×1.74 around the same
   centre). Measured references for the corners:
     · telephone: ITU-T G.712 narrowband channel, 300–3400 Hz;
     · walkie-talkie (PMR446 / FRS, 12.5 kHz channel): audio ~300–3000 Hz,
       hard-limited by the transmitter's deviation limiter → `hard` shaper;
     · megaphone horn: rolls off below ~400 Hz (horn cutoff) and above ~4 kHz
       (compression driver), strong 1–3 kHz presence;
     · cabin / station PA: wider, ~250–5 kHz, softer drive, a room around it;
     · AM broadcast receiver: ~150–4.5 kHz, valve-ish asymmetric drive.
   `ctl` — what the controls jump to when the preset is chosen.
   `extras` — the radio dressing switched on with the preset. */
var PRESETS = {
    megaphone: {
        band: { hp: 420, lp: 4000 }, mid: { f: 1100, g: 8, q: 1.1 }, pres: { f: 2600, g: 5, q: 1.4 },
        horn: { f1: 1150, f2: 2350 }, shaper: 'soft', noise: 'hiss', chime: 'station', trimDb: -1, outDb: -0.7,
        ctl: { drive: 55, band: 50, horn: 55, echo: 22, space: 0, noise: 0, volume: 100 },
        extras: { ptt: false, beep: false, chime: false }
    },
    radio: {
        band: { hp: 520, lp: 2900 }, mid: { f: 1400, g: 6, q: 0.9 }, pres: { f: 2300, g: 4, q: 1.2 },
        horn: { f1: 1250, f2: 2100 }, shaper: 'hard', noise: 'hiss', chime: 'station', trimDb: -2, outDb: -1.1,
        ctl: { drive: 70, band: 50, horn: 10, echo: 0, space: 0, noise: 35, volume: 100 },
        extras: { ptt: true, beep: true, chime: false }
    },
    phone: {
        band: { hp: 320, lp: 3400 }, mid: { f: 1700, g: 3, q: 0.8 }, pres: { f: 2900, g: 2, q: 1.0 },
        horn: { f1: 1000, f2: 2000 }, shaper: 'phone', noise: 'hiss', chime: 'station', trimDb: 0, outDb: 2.9,
        ctl: { drive: 20, band: 50, horn: 0, echo: 0, space: 0, noise: 6, volume: 100 },
        extras: { ptt: false, beep: false, chime: false }
    },
    station: {
        band: { hp: 300, lp: 5200 }, mid: { f: 1000, g: 6, q: 1.0 }, pres: { f: 2400, g: 4, q: 1.2 },
        horn: { f1: 900, f2: 1900 }, shaper: 'soft', noise: 'hiss', chime: 'station', trimDb: 0, outDb: -4.8,
        ctl: { drive: 30, band: 50, horn: 35, echo: 12, space: 60, noise: 0, volume: 100 },
        extras: { ptt: false, beep: false, chime: true }
    },
    pilot: {
        band: { hp: 300, lp: 4600 }, mid: { f: 1500, g: 5, q: 1.0 }, pres: { f: 2800, g: 3, q: 1.3 },
        horn: { f1: 1300, f2: 2600 }, shaper: 'soft', noise: 'cabin', chime: 'cabin', trimDb: 0, outDb: -1.1,
        ctl: { drive: 25, band: 50, horn: 15, echo: 0, space: 8, noise: 40, volume: 100 },
        extras: { ptt: false, beep: false, chime: true }
    },
    vintage: {
        band: { hp: 180, lp: 4200 }, mid: { f: 900, g: 4, q: 0.7 }, pres: { f: 2000, g: 3, q: 1.0 },
        horn: { f1: 800, f2: 1700 }, shaper: 'tube', noise: 'crackle', chime: 'station', trimDb: 0, outDb: -0.6,
        ctl: { drive: 40, band: 45, horn: 0, echo: 0, space: 10, noise: 45, volume: 100 },
        extras: { ptt: false, beep: false, chime: false }
    }
};
var PRESET_ORDER = ['megaphone', 'radio', 'phone', 'station', 'pilot', 'vintage'];
var CTL_KEYS = ['drive', 'band', 'horn', 'echo', 'space', 'noise', 'volume'];
var CTL_RANGE = { drive: [0, 100], band: [0, 100], horn: [0, 100], echo: [0, 100], space: [0, 100], noise: [0, 100], volume: [25, 200] };

/** Full parameter set of a preset: {preset, drive…volume, ptt, beep, chime}. */
function presetParams(name) {
    var p = PRESETS[name] || PRESETS.megaphone;
    var out = { preset: PRESETS[name] ? name : 'megaphone' };
    for (var i = 0; i < CTL_KEYS.length; i++) out[CTL_KEYS[i]] = p.ctl[CTL_KEYS[i]];
    out.ptt = !!p.extras.ptt; out.beep = !!p.extras.beep; out.chime = !!p.extras.chime;
    return out;
}

/** Same set, every field present and inside its range. */
function normalize(params) {
    params = params || {};
    var base = presetParams(params.preset);
    var out = { preset: base.preset };
    for (var i = 0; i < CTL_KEYS.length; i++) {
        var k = CTL_KEYS[i], r = CTL_RANGE[k];
        out[k] = clamp(num(params[k], base[k]), r[0], r[1]);
    }
    out.ptt = params.ptt == null ? base.ptt : !!params.ptt;
    out.beep = params.beep == null ? base.beep : !!params.beep;
    out.chime = params.chime == null ? base.chime : !!params.chime;
    return out;
}

/** True when the controls are exactly the preset's — the UI says «Свой» otherwise. */
function matchesPreset(params) {
    var n = normalize(params), b = presetParams(n.preset);
    for (var i = 0; i < CTL_KEYS.length; i++) if (Math.round(n[CTL_KEYS[i]]) !== b[CTL_KEYS[i]]) return false;
    return n.ptt === b.ptt && n.beep === b.beep && n.chime === b.chime;
}

/* ── the mapping: controls → node values (pure) ──────────────────────────── */
function plan(params, sampleRate) {
    var n = normalize(params);
    var P = PRESETS[n.preset];
    var sr = num(sampleRate, 48000);
    var nyq = sr / 2;

    // Passband: the preset's corners, widened or narrowed in octaves about their centre.
    var c = Math.sqrt(P.band.hp * P.band.lp);
    var half = Math.log2(P.band.lp / P.band.hp) / 2;
    half *= Math.pow(2, (n.band - 50) / 50 * 0.8);
    var hp = clamp(c / Math.pow(2, half), 70, 2000);
    var lp = clamp(c * Math.pow(2, half), 1200, Math.min(9000, nyq * 0.9));

    var d = n.drive / 100, h = n.horn / 100, e = n.echo / 100, s = n.space / 100, z = n.noise / 100;
    var driveDb = 26 * d;
    return {
        preset: n.preset,
        hp: hp, lp: lp,
        hp2: hp * 0.85, lp2: Math.min(lp * 1.08, nyq * 0.92),
        qDb: BUTTER_Q_DB,
        q4Db: BUTTER4_Q_DB,
        q8Db: BUTTER8_Q_DB,
        mid: { f: P.mid.f, g: P.mid.g + 4 * d, q: P.mid.q },
        pres: { f: P.pres.f, g: P.pres.g + 3 * d, q: P.pres.q },
        comp: { threshold: -12 - 20 * d, ratio: 2 + 10 * d, knee: 6, attack: 0.004, release: 0.1 },
        shaper: P.shaper,
        driveDb: driveDb,
        // Partial make-up: the shaper is unity for small signals, so the drive gain
        // also lifts quiet syllables; give back 35 % of it so «Перегруз» is mostly
        // grit, not volume.
        postDriveDb: -0.35 * driveDb,
        horn: {
            p1: { f: P.horn.f1, g: 10 * h, q: 3.5 },
            p2: { f: P.horn.f2, g: 6 * h, q: 4 },
            // ≥ 3 ms: a DelayNode inside a cycle is clamped to one render quantum
            // (128 frames = 2.9 ms at 44.1 kHz), so a shorter comb would quietly
            // be a different comb at each sample rate.
            delay: (4.2 - 1.2 * h) / 1000,
            fb: 0.4 * h,                    // < 1 — the loop's filter never gains (Butterworth)
            wet: 0.5 * h,
            lp: 3500
        },
        echo: {
            delay: (70 + 160 * e) / 1000,
            fb: 0.45 * e,
            wet: 0.6 * e,
            lp: 3000
        },
        space: { wet: 0.6 * s, size: s < 0.35 ? 'small' : (s < 0.7 ? 'medium' : 'large'), predelay: 0.028 },
        noise: { type: P.noise, db: z > 0 ? -60 + 40 * z : -Infinity },
        trimDb: P.trimDb,
        // Output make-up per preset: measured so every preset's loud speech lands
        // near −10 dB (90th pct of 50 ms RMS) on three voices — a phone line is
        // 4 dB quieter than a horn by nature, and a preset that is louder wins an
        // A/B for being louder, not for sounding right.
        volume: n.volume / 100 * db2g(P.outDb || 0),
        limiter: { threshold: -2, ratio: 20, knee: 0, attack: 0.001, release: 0.06 },
        stereo: s > 0
    };
}

var SPACE_LEN = { small: 0.9, medium: 1.6, large: 2.6 };

/** Seconds every stateful stage needs to forget what came before a window. */
function prerollSec(params) {
    var p = plan(params, 48000);
    var t = 1.0;
    var echoT = 0;
    if (p.echo.fb > 0.001) {
        var perRepeatDb = -g2db(p.echo.fb);              // loop gain ≤ fb (the lowpass never gains)
        echoT = p.echo.delay * Math.ceil(90 / perRepeatDb);
    }
    // The hall hears the echo: its memory is the echo's AND the room's.
    var hallT = p.space.wet > 0 ? SPACE_LEN[p.space.size] + p.space.predelay : 0;
    t = Math.max(t, echoT + hallT + 0.25);
    return Math.min(t, 10);
}

/** How long the effect keeps ringing after the last input (for the tail of the file). */
function ringSec(params) {
    var p = plan(params, 48000);
    var t = 0.15, e = 0;
    if (p.echo.wet > 0.001 && p.echo.fb > 0.001) {
        var perRepeatDb = -g2db(p.echo.fb);
        e = p.echo.delay * Math.ceil(55 / perRepeatDb);
    } else if (p.echo.wet > 0.001) e = p.echo.delay + 0.05;
    var h = p.space.wet > 0 ? SPACE_LEN[p.space.size] * 0.9 + p.space.predelay : 0;
    t = Math.max(t, e + h * (e ? 0.6 : 1));
    return Math.min(t, 4.5);
}
var END_FADE_S = 0.06;   // the file ends on a fade, never on a cut reverb tail

/* ── curves ───────────────────────────────────────────────────────────── */
function satP(x, p) { return x / Math.pow(1 + Math.pow(Math.abs(x), p), 1 / p); }
/** The drive stage. Input of the table spans ±SHAPER_DOMAIN (the node is fed 1/domain). */
function shaperCurve(kind, n) {
    n = n || 4097;
    var curve = new Float32Array(n), D = SHAPER_DOMAIN;
    var f;
    if (kind === 'hard') f = function (x) { return satP(x + 0.05, 10) - satP(0.05, 10); };
    else if (kind === 'phone') f = function (x) { return satP(x, 2); };
    else if (kind === 'tube') f = function (x) { return Math.tanh(x + 0.25) - Math.tanh(0.25); };
    else f = function (x) { return satP(x + 0.12, 2.5) - satP(0.12, 2.5); };
    for (var i = 0; i < n; i++) curve[i] = f((i * 2 / (n - 1) - 1) * D);
    return curve;
}
/** Output ceiling: p-norm saturator, asymptotic to ±ceil (see the 2026-07-31 note in the blade's history). */
function ceilingCurve(ceil, domain, p, n) {
    n = n || 8193;
    var curve = new Float32Array(n);
    for (var i = 0; i < n; i++) {
        var x = (i * 2 / (n - 1) - 1) * domain;
        curve[i] = x / Math.pow(1 + Math.pow(Math.abs(x) / ceil, p), 1 / p);
    }
    return curve;
}

/* ── deterministic noise ─────────────────────────────────────────────── */
function rng(seed) {
    var a = seed >>> 0;
    return function () {
        a = (a + 0x6D2B79F5) >>> 0;
        var t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
var NOISE_LOOP_S = 4;     // 50 Hz and 60 Hz hum both close exactly on 4 s
/** A seamless loop of the preset's background, normalised to 0 dBFS RMS-ish. */
function noiseLoop(type, sr) {
    // Generated FADE samples longer than the loop: the loop's first FADE samples
    // are then cross-faded with the samples that FOLLOW its end, so the wrap
    // x[n−1] → x[0] continues the sequence y[n−1] → y[n] instead of jumping.
    var n = Math.round(NOISE_LOOP_S * sr), fade = Math.round(0.02 * sr), m = n + fade;
    var y = new Float32Array(m), r = rng(type === 'cabin' ? 7 : (type === 'crackle' ? 11 : 3));
    var i;
    if (type === 'cabin') {
        // Cabin: brown noise (jet rumble) + a little 120 Hz from the cabin's electrics.
        var b = 0;
        for (i = 0; i < m; i++) { b = 0.995 * b + 0.06 * (r() * 2 - 1); y[i] = b + 0.05 * Math.sin(2 * Math.PI * 120 * i / sr); }
    } else if (type === 'crackle') {
        // Old radio: 50 Hz hum with its 3rd harmonic, a floor of hiss, sparse crackles.
        for (i = 0; i < m; i++) y[i] = 0.25 * Math.sin(2 * Math.PI * 50 * i / sr) + 0.1 * Math.sin(2 * Math.PI * 150 * i / sr) + 0.18 * (r() * 2 - 1);
        var pops = Math.round(9 * NOISE_LOOP_S);
        for (var k = 0; k < pops; k++) {
            var at = Math.floor(r() * (n - 200)), amp = (0.4 + 1.6 * r()) * (r() < 0.5 ? -1 : 1), len = 20 + Math.floor(r() * 120);
            for (var j = 0; j < len; j++) y[at + j] += amp * Math.exp(-j / (len / 5)) * (r() * 2 - 1);
        }
    } else {
        for (i = 0; i < m; i++) y[i] = r() * 2 - 1;
    }
    var x = new Float32Array(n);
    for (i = 0; i < n; i++) x[i] = y[i];
    for (i = 0; i < fade; i++) { var w = i / fade; x[i] = y[i] * w + y[n + i] * (1 - w); }
    var mean = 0; for (i = 0; i < n; i++) mean += x[i]; mean /= n;
    var e = 0; for (i = 0; i < n; i++) { x[i] -= mean; e += x[i] * x[i]; }
    var g = 1 / Math.sqrt(e / n || 1) * 0.25;            // RMS −12 dBFS before the level control
    for (i = 0; i < n; i++) x[i] *= g;
    return x;
}

/* ── one-shot sounds of the dressing ────────────────────────────────────
   tx = goes in through the effect (the transmitter / the PA amplifies it);
   rx = the receiver's own sound (squelch, keying click), joins after the drive. */
var EVENT_DEST = { chime_station: 'tx', chime_cabin: 'tx', beep: 'tx', click: 'rx', squelch: 'rx' };
function bell(x, sr, at, f, amp, decay) {
    var partials = [[1, 1], [2.0, 0.45], [3.01, 0.22], [4.2, 0.12]];
    var n0 = Math.round(at * sr), len = Math.min(x.length - n0, Math.round(decay * 4 * sr));
    for (var i = 0; i < len; i++) {
        var t = i / sr, env = Math.min(1, t / 0.004) * Math.exp(-t / decay), v = 0;
        for (var k = 0; k < partials.length; k++) v += partials[k][1] * Math.sin(2 * Math.PI * f * partials[k][0] * t) * Math.exp(-t * k * 0.9);
        x[n0 + i] += amp * env * v;
    }
}
function eventSamples(kind, sr) {
    var x, i, r = rng(kind.length * 101);
    if (kind === 'chime_station') {
        // Three rising notes — the station «внимание» chime.
        x = new Float32Array(Math.round(CHIME_LEN.station * sr));
        bell(x, sr, 0.00, 523.25, 0.28, 0.55); bell(x, sr, 0.42, 659.25, 0.28, 0.55); bell(x, sr, 0.84, 783.99, 0.3, 0.7);
    } else if (kind === 'chime_cabin') {
        // Two notes, high then low — the cabin «дин-дон».
        x = new Float32Array(Math.round(CHIME_LEN.cabin * sr));
        bell(x, sr, 0.00, 880, 0.3, 0.6); bell(x, sr, 0.62, 739.99, 0.3, 0.75);
    } else if (kind === 'beep') {
        // Roger beep: two short tones.
        x = new Float32Array(Math.round(0.18 * sr));
        for (i = 0; i < x.length; i++) {
            // Square-ish, but band-limited (three odd harmonics): a true square
            // aliases its upper harmonics back into the band at 44.1/48 kHz.
            var t = i / sr, f = t < 0.07 ? 1100 : 1650, env = Math.min(1, Math.min(t, 0.18 - t) / 0.004), ph = 2 * Math.PI * f * t;
            x[i] = 0.3 * env * (Math.sin(ph) + Math.sin(3 * ph) / 3 + Math.sin(5 * ph) / 5);
        }
    } else if (kind === 'click') {
        // The key: a short thump with a burst of noise.
        x = new Float32Array(Math.round(0.06 * sr));
        for (i = 0; i < x.length; i++) { var tt = i / sr; x[i] = (0.6 * (r() * 2 - 1) * Math.exp(-tt / 0.004) + 0.4 * Math.sin(2 * Math.PI * 160 * tt) * Math.exp(-tt / 0.012)); }
    } else {
        // Squelch tail: «кшшш» that is cut, not faded — the receiver closes.
        x = new Float32Array(Math.round(SQUELCH_S * sr));
        var att = Math.round(0.006 * sr), rel = Math.round(0.004 * sr);
        for (i = 0; i < x.length; i++) {
            var env2 = Math.min(1, i / att, (x.length - i) / rel);
            x[i] = 0.32 * env2 * (r() * 2 - 1);
        }
    }
    return x;
}
var CHIME_LEN = { station: 2.6, cabin: 2.4 };
var SQUELCH_S = 0.3;

/* ── timeline: where voice and dressing sit (pure) ───────────────────────
   extend=true (audio): the chime and the key come BEFORE the voice, the roger
   beep and the squelch AFTER it, and the effect rings out at the end.
   extend=false (video): nothing may move the voice off the picture — the
   dressing is laid inside the clip's own length. Times are on integer frames. */
function timeline(o) {
    var sr = num(o.sampleRate, 48000);
    var p = normalize(o.params);
    var P = PRESETS[p.preset];
    var fr = function (t) { return Math.round(t * sr) / sr; };
    var dur = Math.max(0, num(o.duration, 0));
    var extend = o.extend !== false;
    var chimeLen = CHIME_LEN[P.chime] || CHIME_LEN.station;
    var ev = [];
    var lead = 0, tail = 0;
    var voiceAt = 0;
    if (extend) {
        if (p.chime) lead += chimeLen * 0.62;         // the voice starts while the last note rings
        if (p.ptt) lead += 0.22;
        lead = fr(lead);
        voiceAt = lead;
        if (p.chime) ev.push({ kind: 'chime_' + P.chime, t: 0 });
        if (p.ptt) ev.push({ kind: 'click', t: fr(lead - 0.2) });
        var after = fr(voiceAt + dur);
        var tEnd = after;
        if (p.beep) { ev.push({ kind: 'beep', t: fr(after + 0.05) }); tEnd = after + 0.05 + 0.18; }
        if (p.ptt) { ev.push({ kind: 'squelch', t: fr(tEnd + 0.03) }); tEnd = tEnd + 0.03 + SQUELCH_S; }
        tail = fr(Math.max(tEnd - after, 0) + ringSec(p));
    } else {
        if (p.chime) ev.push({ kind: 'chime_' + P.chime, t: 0 });
        if (p.ptt) ev.push({ kind: 'click', t: 0 });
        if (p.beep) ev.push({ kind: 'beep', t: fr(Math.max(0, dur - 0.18 - (p.ptt ? SQUELCH_S + 0.03 : 0) - 0.02)) });
        if (p.ptt) ev.push({ kind: 'squelch', t: fr(Math.max(0, dur - SQUELCH_S - 0.02)) });
    }
    var total = fr(voiceAt + dur + tail);
    for (var i = 0; i < ev.length; i++) ev[i].dest = EVENT_DEST[ev[i].kind];
    // The background is heard while the channel is open: from the key to the
    // squelch with a key, otherwise the whole clip.
    var gate = null;
    if (p.noise > 0) {
        if (p.ptt && extend) {
            var k = ev.filter(function (e) { return e.kind === 'click'; })[0];
            var q = ev.filter(function (e) { return e.kind === 'squelch'; })[0];
            gate = [k ? k.t : 0, q ? fr(q.t + SQUELCH_S) : total];
        } else gate = [0, total];
    }
    return { lead: voiceAt, voiceAt: voiceAt, dur: dur, tail: tail, total: total, events: ev, gate: gate, sampleRate: sr, extend: extend };
}

/* ── the level a file's speech is put at before the chain ───────────────
   The effect's character depends on how hard the voice hits the drive, and a
   whispered phone memo and a mastered podcast differ by 30 dB. The 90th
   percentile of 50 ms block RMS is the «loud speech» level; pauses do not
   count. */
function speechLevelDb(channels, sr) {
    var n = channels[0].length, bl = Math.max(64, Math.round(0.05 * sr)), lv = [];
    for (var s = 0; s + bl <= n; s += bl) {
        var e = 0;
        for (var i = s; i < s + bl; i++) {
            var v = 0;
            for (var c = 0; c < channels.length; c++) v += channels[c][i];
            v /= channels.length;
            e += v * v;
        }
        var db = 10 * Math.log10(e / bl + 1e-20);
        if (db > -60) lv.push(db);
    }
    if (!lv.length) return -60;
    lv.sort(function (a, b) { return a - b; });
    return lv[Math.min(lv.length - 1, Math.floor(lv.length * 0.9))];
}
function trimFor(levelDb) { return clamp(SPEECH_TARGET_DB - levelDb, -12, 24); }

/* ── response of the linear part (for the display) ──────────────────────
   Asked of the browser's own nodes (BiquadFilterNode.getFrequencyResponse) —
   the filters the sound really goes through, not a second copy of their
   formula (EqHeadroomSinglePrimitiveTest). dB of band + presence + horn peaks
   at each frequency, no drive, no loops; null where there is no Web Audio. */
var respCtx = {};
function responseDb(params, freqs, sr) {
    sr = num(sr, 48000);
    var C = typeof OfflineAudioContext !== 'undefined' ? OfflineAudioContext
        : (typeof window !== 'undefined' && window.webkitOfflineAudioContext);
    if (!C) return null;
    var ctx = respCtx[sr] || (respCtx[sr] = new C(1, 128, sr));
    var p = plan(params, sr), n = freqs.length;
    var f = freqs instanceof Float32Array ? freqs : Float32Array.from(freqs);
    var mag = new Float32Array(n), ph = new Float32Array(n), acc = new Float32Array(n), i;
    for (i = 0; i < n; i++) acc[i] = 1;
    var specs = [
        ['highpass', p.hp, p.qDb], ['lowpass', p.lp, p.qDb],
        ['peaking', p.mid.f, p.mid.q, p.mid.g], ['peaking', p.pres.f, p.pres.q, p.pres.g],
        ['highpass', p.hp2, p.q4Db[0]], ['highpass', p.hp2, p.q4Db[1]],
        ['lowpass', p.lp2, p.q8Db[0]], ['lowpass', p.lp2, p.q8Db[1]], ['lowpass', p.lp2, p.q8Db[2]], ['lowpass', p.lp2, p.q8Db[3]],
        ['peaking', p.horn.p1.f, p.horn.p1.q, p.horn.p1.g], ['peaking', p.horn.p2.f, p.horn.p2.q, p.horn.p2.g]
    ];
    for (var k = 0; k < specs.length; k++) {
        var sp = specs[k], b = biquad(ctx, sp[0], sp[1], sp[2], sp[3]);
        b.getFrequencyResponse(f, mag, ph);
        for (i = 0; i < n; i++) acc[i] *= mag[i];
    }
    for (i = 0; i < n; i++) acc[i] = g2db(acc[i]);
    return acc;
}

/* ── the graph ────────────────────────────────────────────────────────── */
var cache = { shaper: {}, ceiling: null, noise: {}, ev: {}, ir: {} };
function shaperFor(kind) { return cache.shaper[kind] || (cache.shaper[kind] = shaperCurve(kind)); }
function ceilingFor() { return cache.ceiling || (cache.ceiling = ceilingCurve(CEIL_LIMIT, CEIL_DOMAIN, 6)); }
function bufferOf(ctx, data, channels) {
    var b = ctx.createBuffer(channels ? channels.length : 1, (channels ? channels[0] : data).length, ctx.sampleRate);
    if (channels) for (var c = 0; c < channels.length; c++) b.copyToChannel(channels[c], c);
    else b.copyToChannel(data, 0);
    return b;
}
function noiseData(type, sr) { var k = type + '@' + sr; return cache.noise[k] || (cache.noise[k] = noiseLoop(type, sr)); }
function eventData(kind, sr) { var k = kind + '@' + sr; return cache.ev[k] || (cache.ev[k] = eventSamples(kind, sr)); }
/** Stereo hall: decorrelated exponentially-decaying noise, darker as it decays, a few early reflections. */
function irData(size, sr) {
    var k = size + '@' + sr;
    if (cache.ir[k]) return cache.ir[k];
    var len = SPACE_LEN[size], n = Math.round(len * sr), out = [new Float32Array(n), new Float32Array(n)];
    for (var ch = 0; ch < 2; ch++) {
        var r = rng(31 + ch * 17), x = out[ch], lpState = 0;
        for (var i = 0; i < n; i++) {
            var t = i / sr, env = Math.exp(-6.9 * t / len);
            var a = 0.25 + 0.7 * (t / len);               // one-pole smoothing grows: high end dies first
            lpState += (1 - a) * ((r() * 2 - 1) - lpState);
            x[i] = lpState * env;
        }
        var taps = [0.011, 0.019, 0.027, 0.041, 0.053];
        for (var j = 0; j < taps.length; j++) {
            var at = Math.round((taps[j] + ch * 0.0037) * sr);
            if (at < n) x[at] += (0.55 - j * 0.08) * (j % 2 ? -1 : 1);
        }
    }
    cache.ir[k] = out;
    return out;
}

function biquad(ctx, type, f, q, g) {
    var b = ctx.createBiquadFilter();
    b.type = type; b.frequency.value = f; b.Q.value = q; if (g != null) b.gain.value = g;
    return b;
}
function gain(ctx, v) { var g = ctx.createGain(); g.gain.value = v; return g; }

/**
 * Build the effect in `ctx`. Returns
 *   { input, rx, room, output, set(params), setTrim(db), dispose(), params, plan }
 * input — the voice and the transmitted dressing (mono, downmixed);
 * rx    — the receiver's own sounds (squelch, key click, hiss) — after the drive;
 * room  — sounds of the room the speaker is in (cabin noise) — after the horn.
 * opts.trimDb — input trim (file: trimFor(speechLevelDb); mic: LIVE_TRIM_DB).
 */
function build(ctx, params, opts) {
    opts = opts || {};
    var sr = ctx.sampleRate;
    var N = {};
    var p = plan(params, sr);
    var cur = normalize(params);

    N.input = gain(ctx, 1);
    N.input.channelCount = 1; N.input.channelCountMode = 'explicit'; N.input.channelInterpretation = 'speakers';
    N.trim = gain(ctx, db2g(num(opts.trimDb, 0) + p.trimDb));
    N.hp1 = biquad(ctx, 'highpass', p.hp, p.qDb);
    N.lp1 = biquad(ctx, 'lowpass', p.lp, p.qDb);
    N.mid = biquad(ctx, 'peaking', p.mid.f, p.mid.q, p.mid.g);
    N.pres = biquad(ctx, 'peaking', p.pres.f, p.pres.q, p.pres.g);
    N.comp = ctx.createDynamicsCompressor();
    N.drive = gain(ctx, db2g(p.driveDb) / SHAPER_DOMAIN);
    N.shaper = ctx.createWaveShaper(); N.shaper.curve = shaperFor(p.shaper); N.shaper.oversample = '4x';
    N.postDrive = gain(ctx, db2g(p.postDriveDb));
    N.rx = gain(ctx, 1);                                   // receiver sounds join here
    // After the drive the band is cut hard — 24 dB/oct below, 48 dB/oct above
    // (4th- and 8th-order Butterworth). The drive's harmonics are what makes a
    // line sound wide: at 12 dB/oct a phone preset still drew harmonics up to
    // 9 kHz on the spectrogram, at 24 dB/oct energy above 1.6×corner was −41…−46 dB
    // (2026-10-08). A phone channel is a codec's brick wall, a horn driver falls
    // off a cliff above its range.
    N.hp2 = biquad(ctx, 'highpass', p.hp2, p.q4Db[0]);
    N.hp2b = biquad(ctx, 'highpass', p.hp2, p.q4Db[1]);
    N.lp2 = biquad(ctx, 'lowpass', p.lp2, p.q8Db[0]);
    N.lp2b = biquad(ctx, 'lowpass', p.lp2, p.q8Db[1]);
    N.lp2c = biquad(ctx, 'lowpass', p.lp2, p.q8Db[2]);
    N.lp2d = biquad(ctx, 'lowpass', p.lp2, p.q8Db[3]);
    N.horn1 = biquad(ctx, 'peaking', p.horn.p1.f, p.horn.p1.q, p.horn.p1.g);
    N.horn2 = biquad(ctx, 'peaking', p.horn.p2.f, p.horn.p2.q, p.horn.p2.g);
    // Horn comb: x + wet·y,  y = delay(x + fb·LP(y))
    N.hornSum = gain(ctx, 1);
    N.hornDelay = ctx.createDelay(0.05);
    N.hornLp = biquad(ctx, 'lowpass', p.horn.lp, p.qDb);
    N.hornFb = gain(ctx, p.horn.fb);
    N.hornWet = gain(ctx, p.horn.wet);
    // Echo: x + wet·y,  y = delay(x + fb·LP(y))
    N.echoSum = gain(ctx, 1);
    N.echoDelay = ctx.createDelay(0.5);
    N.echoLp = biquad(ctx, 'lowpass', p.echo.lp, p.qDb);
    N.echoFb = gain(ctx, p.echo.fb);
    N.echoWet = gain(ctx, p.echo.wet);
    N.room = gain(ctx, 1);                                 // sounds of the room around the speaker
    // Hall (stereo from here on)
    N.spacePre = ctx.createDelay(0.2);
    N.spaceConv = ctx.createConvolver();
    N.spaceWet = gain(ctx, p.space.wet);
    N.mix = gain(ctx, 1);
    N.vol = gain(ctx, p.volume);
    N.lim = ctx.createDynamicsCompressor();
    N.ceilPre = gain(ctx, 1 / CEIL_DOMAIN);
    N.ceil = ctx.createWaveShaper(); N.ceil.curve = ceilingFor(); N.ceil.oversample = '2x';
    N.output = gain(ctx, 1);

    N.input.connect(N.trim); N.trim.connect(N.hp1); N.hp1.connect(N.lp1); N.lp1.connect(N.mid); N.mid.connect(N.pres);
    N.pres.connect(N.comp); N.comp.connect(N.drive); N.drive.connect(N.shaper); N.shaper.connect(N.postDrive);
    N.postDrive.connect(N.rx); N.rx.connect(N.hp2); N.hp2.connect(N.hp2b); N.hp2b.connect(N.lp2); N.lp2.connect(N.lp2b); N.lp2b.connect(N.lp2c); N.lp2c.connect(N.lp2d); N.lp2d.connect(N.horn1); N.horn1.connect(N.horn2);
    N.horn2.connect(N.hornSum); N.horn2.connect(N.hornDelay);
    N.hornDelay.connect(N.hornLp); N.hornLp.connect(N.hornFb); N.hornFb.connect(N.hornDelay);
    N.hornDelay.connect(N.hornWet); N.hornWet.connect(N.hornSum);
    N.hornSum.connect(N.echoSum); N.hornSum.connect(N.echoDelay);
    N.echoDelay.connect(N.echoLp); N.echoLp.connect(N.echoFb); N.echoFb.connect(N.echoDelay);
    N.echoDelay.connect(N.echoWet); N.echoWet.connect(N.echoSum);
    N.echoSum.connect(N.room);
    N.room.connect(N.mix);
    N.room.connect(N.spacePre); N.spacePre.connect(N.spaceConv); N.spaceConv.connect(N.spaceWet); N.spaceWet.connect(N.mix);
    N.mix.connect(N.vol); N.vol.connect(N.lim); N.lim.connect(N.ceilPre); N.ceilPre.connect(N.ceil); N.ceil.connect(N.output);

    var irSize = null;
    function applyStatic(pl) {
        N.hornDelay.delayTime.value = pl.horn.delay;
        N.echoDelay.delayTime.value = pl.echo.delay;
        N.spacePre.delayTime.value = pl.space.predelay;
        var c = pl.comp, l = pl.limiter;
        N.comp.threshold.value = c.threshold; N.comp.ratio.value = c.ratio; N.comp.knee.value = c.knee;
        N.comp.attack.value = c.attack; N.comp.release.value = c.release;
        // knee MUST be set on the limiter: Web Audio's default 30 dB knee turns a
        // −2 dB threshold into −0.36 dB of reduction (LimiterKneeConventionTest).
        N.lim.threshold.value = l.threshold; N.lim.ratio.value = l.ratio; N.lim.knee.value = l.knee;
        N.lim.attack.value = l.attack; N.lim.release.value = l.release;
        if (pl.space.wet > 0 && irSize !== pl.space.size) {
            irSize = pl.space.size;
            N.spaceConv.buffer = bufferOf(ctx, null, irData(irSize, sr));
        }
    }
    applyStatic(p);

    var api = {
        ctx: ctx, nodes: N, input: N.input, rx: N.rx, room: N.room, output: N.output,
        params: cur, plan: p, trimDb: num(opts.trimDb, 0),
        /** Move to new parameters. Live contexts glide (20 ms) so a dragged slider does not click. */
        set: function (params2) {
            cur = normalize(params2); var pl = plan(cur, sr); api.params = cur; api.plan = pl;
            var live = !(typeof OfflineAudioContext !== 'undefined' && ctx instanceof OfflineAudioContext);
            var t = ctx.currentTime;
            function to(param, v) {
                if (!live) { param.value = v; return; }
                try { param.cancelScheduledValues(t); param.setTargetAtTime(v, t, 0.02); } catch (e) { param.value = v; }
            }
            to(N.trim.gain, db2g(api.trimDb + pl.trimDb));
            to(N.hp1.frequency, pl.hp); to(N.lp1.frequency, pl.lp);
            to(N.hp2.frequency, pl.hp2); to(N.hp2b.frequency, pl.hp2); to(N.lp2.frequency, pl.lp2); to(N.lp2b.frequency, pl.lp2);
            to(N.lp2c.frequency, pl.lp2); to(N.lp2d.frequency, pl.lp2);
            N.mid.frequency.value = pl.mid.f; N.mid.Q.value = pl.mid.q; to(N.mid.gain, pl.mid.g);
            N.pres.frequency.value = pl.pres.f; N.pres.Q.value = pl.pres.q; to(N.pres.gain, pl.pres.g);
            to(N.drive.gain, db2g(pl.driveDb) / SHAPER_DOMAIN); to(N.postDrive.gain, db2g(pl.postDriveDb));
            if (N.shaper.__kind !== pl.shaper) { N.shaper.curve = shaperFor(pl.shaper); N.shaper.__kind = pl.shaper; }
            N.horn1.frequency.value = pl.horn.p1.f; to(N.horn1.gain, pl.horn.p1.g);
            N.horn2.frequency.value = pl.horn.p2.f; to(N.horn2.gain, pl.horn.p2.g);
            to(N.hornFb.gain, pl.horn.fb); to(N.hornWet.gain, pl.horn.wet);
            to(N.echoFb.gain, pl.echo.fb); to(N.echoWet.gain, pl.echo.wet);
            to(N.spaceWet.gain, pl.space.wet);
            to(N.vol.gain, pl.volume);
            applyStatic(pl);
            if (api.onPlan) try { api.onPlan(pl); } catch (e) {}
        },
        setTrim: function (db) { api.trimDb = num(db, 0); N.trim.gain.value = db2g(api.trimDb + api.plan.trimDb); },
        dispose: function () { for (var k in N) if (N[k] && N[k].disconnect) { try { N[k].disconnect(); } catch (e) {} } }
    };
    N.shaper.__kind = p.shaper;
    return api;
}

/* ── putting sources on the timeline: LIVE preview ──────────────────────
   fromT — timeline second that plays at ctx time `when`; toT — where to stop.
   The offline export does NOT use this — see layWindow().
   src = { voice: AudioBuffer|null } — the dressing is made here.
   Returns { stop(), sources }. */
function noiseGateValue(gate, level, t) { return gate && t >= gate[0] && t < gate[1] ? level : 0; }
function schedule(ctx, eng, tl, src, fromT, when, toT) {
    var sr = ctx.sampleRate, list = [];
    toT = toT == null ? tl.total : toT;
    function start(node, at, off, dur) { try { node.start(at, off, dur); } catch (e) { node.start(at, off); } list.push(node); }
    // Voice
    if (src && src.voice && fromT < tl.voiceAt + tl.dur && toT > tl.voiceAt) {
        var v = ctx.createBufferSource(); v.buffer = src.voice; v.connect(eng.input);
        if (src.voiceTap) v.connect(src.voiceTap);
        var vOff = Math.max(0, fromT - tl.voiceAt), vAt = when + Math.max(0, tl.voiceAt - fromT);
        start(v, vAt, vOff, Math.max(0, Math.min(tl.dur, toT - tl.voiceAt) - vOff));
    }
    // Dressing
    for (var i = 0; i < tl.events.length; i++) {
        var e = tl.events[i], data = eventData(e.kind, sr), len = data.length / sr;
        if (e.t + len <= fromT || e.t >= toT) continue;
        var b = ctx.createBufferSource(); b.buffer = bufferOf(ctx, data);
        b.connect(e.dest === 'rx' ? eng.rx : eng.input);
        start(b, when + Math.max(0, e.t - fromT), Math.max(0, fromT - e.t));
    }
    // Background
    var pl = eng.plan;
    if (tl.gate && isFinite(pl.noise.db)) {
        var nb = ctx.createBufferSource(); nb.buffer = bufferOf(ctx, noiseData(pl.noise.type, sr)); nb.loop = true;
        var ng = gain(ctx, 0);
        var level = db2g(pl.noise.db);
        nb.connect(ng); ng.connect(pl.noise.type === 'cabin' ? eng.room : eng.rx);
        ng.gain.setValueAtTime(noiseGateValue(tl.gate, level, fromT), when);
        for (var gi = 0; gi < 2; gi++) {
            var gt = tl.gate[gi];
            if (gt > fromT && gt < toT) {
                var at = when + (gt - fromT);
                ng.gain.setValueAtTime(gi === 0 ? 0 : level, Math.max(when, at - 0.005));
                ng.gain.linearRampToValueAtTime(gi === 0 ? level : 0, at);
            }
        }
        var loopLen = nb.buffer.duration;
        start(nb, when, ((fromT % loopLen) + loopLen) % loopLen);
        list.push(ng);
    }
    return {
        sources: list,
        stop: function () {
            for (var k = 0; k < list.length; k++) {
                try { if (list[k].stop) list[k].stop(); } catch (e) {}
                try { list[k].disconnect(); } catch (e) {}
            }
            list = [];
        }
    };
}

/** Fire one piece of dressing now (live microphone: «гонг», «приём»). */
function fire(ctx, eng, kind, when) {
    var data = eventData(kind, ctx.sampleRate), b = ctx.createBufferSource();
    b.buffer = bufferOf(ctx, data);
    b.connect(EVENT_DEST[kind] === 'rx' ? eng.rx : eng.input);
    b.start(when == null ? ctx.currentTime + 0.02 : when);
    return b;
}
/** Continuous background for the live microphone (no gate: the channel is open). */
function liveNoise(ctx, eng) {
    var nb = ctx.createBufferSource(), ng = gain(ctx, 0), type = null;
    nb.loop = true;
    function apply(pl) {
        if (pl.noise.type !== type) {
            type = pl.noise.type;
            try { nb.disconnect(); } catch (e) {}
            nb.connect(ng);
            try { ng.disconnect(); } catch (e) {}
            ng.connect(type === 'cabin' ? eng.room : eng.rx);
        }
        var v = isFinite(pl.noise.db) ? db2g(pl.noise.db) : 0;
        try { ng.gain.setTargetAtTime(v, ctx.currentTime, 0.03); } catch (e) { ng.gain.value = v; }
    }
    nb.buffer = bufferOf(ctx, noiseData(eng.plan.noise.type, ctx.sampleRate));
    apply(eng.plan);
    nb.start();
    return {
        update: function (pl) {
            if (pl.noise.type !== type) { try { nb.buffer = bufferOf(ctx, noiseData(pl.noise.type, ctx.sampleRate)); } catch (e) {} }
            apply(pl);
        },
        stop: function () { try { nb.stop(); } catch (e) {} try { nb.disconnect(); ng.disconnect(); } catch (e) {} }
    };
}

/* ── laying a window out for the offline export ─────────────────────────
   Every source of a window spans the WHOLE window and is filled by frame
   number: the voice (its own channels, zero outside the voice), the
   transmitted dressing, the receiver's sounds with the gated background, the
   room's background. Measured 2026-10-08: with sources that START and END
   inside the render (the live scheduler's way), Chromium switches off the
   nodes behind a finished source after their tail time, and a window and a
   single render of the same timeline differed by up to −28 dB around 1.7 s
   after the voice ended; a looping background started mid-buffer differed by
   −35 dB at its wrap. With full-length sources laid by frame, a window equals
   the single render to the float. */
function gateEnv(gate, t) {
    if (!gate) return 0;
    var r = 0.005;
    if (t < gate[0] - r || t >= gate[1]) return 0;
    if (t < gate[0]) return (t - (gate[0] - r)) / r;
    if (t >= gate[1] - r) return (gate[1] - t) / r;
    return 1;
}
function layWindow(ctx, eng, tl, voice, f0, nWin, rxLead) {
    var sr = ctx.sampleRate, pl = eng.plan;
    var vAt = Math.round(tl.voiceAt * sr), vLen = voice ? voice.length : 0;
    // Voice
    if (voice && f0 < vAt + vLen && f0 + nWin > vAt) {
        var vb = ctx.createBuffer(voice.numberOfChannels, nWin, sr);
        var a = Math.max(0, vAt - f0), b0 = Math.max(0, f0 - vAt), n = Math.min(nWin - a, vLen - b0);
        for (var c = 0; c < voice.numberOfChannels; c++) vb.getChannelData(c).set(voice.getChannelData(c).subarray(b0, b0 + n), a);
        var vs = ctx.createBufferSource(); vs.buffer = vb; vs.connect(eng.input); vs.start(0);
    }
    // Dressing
    var tx = null, rx = null, room = null;
    for (var i = 0; i < tl.events.length; i++) {
        var e = tl.events[i], d = eventData(e.kind, sr), at = Math.round(e.t * sr) - f0;
        if (at >= nWin || at + d.length <= 0) continue;
        var dst = e.dest === 'rx' ? (rx = rx || new Float32Array(nWin)) : (tx = tx || new Float32Array(nWin));
        for (var k = Math.max(0, -at); k < d.length && at + k < nWin; k++) dst[at + k] += d[k];
    }
    // Background, gated
    if (tl.gate && isFinite(pl.noise.db)) {
        var loop = noiseData(pl.noise.type, sr), L = loop.length, lvl = db2g(pl.noise.db);
        var dstN = pl.noise.type === 'cabin' ? (room = room || new Float32Array(nWin)) : (rx = rx || new Float32Array(nWin));
        for (var j = 0; j < nWin; j++) {
            var g = gateEnv(tl.gate, (f0 + j) / sr);
            if (g) dstN[j] += lvl * g * loop[(f0 + j) % L];
        }
    }
    // «Приём» и комната входят ПОСЛЕ компрессора и перегруза — позже голоса на их
    // задержку (rxLead), чтобы на выходе стоять там же, где на таймлайне.
    var lanes = [[tx, eng.input, 0], [rx, eng.rx, rxLead || 0], [room, eng.room, rxLead || 0]];
    for (var q = 0; q < lanes.length; q++) {
        if (!lanes[q][0]) continue;
        var s2 = ctx.createBufferSource(); s2.buffer = bufferOf(ctx, lanes[q][0]); s2.connect(lanes[q][1]); s2.start(lanes[q][2] / sr);
    }
}

/**
 * Render `voice` (AudioBuffer) through the effect, window by window.
 * opts: { params, trimDb, extend (default true), channels (1|2, default:
 *         stereo when the hall is on), windowSec, signal (AbortSignal),
 *         onChunk(arrays, info) → may return a Promise, onProgress(frac) }
 * Resolves { sampleRate, channels, frames, timeline }.
 */
/* Задержка цепочки (2026-10-09): компрессор и лимитер — упреждение 6 мс каждый,
   перегруз — передискретизация ×4, потолок — ×2. Голос от входа до выхода — их
   сумма (896 отсчётов при 48 кГц: в ролике голос шёл на 18.7 мс позже губ и терял
   свой конец); «приём» и комната входят после перегруза — им только лимитер и
   потолок. Числа — замером NodeLatency (свои у движка); без него — 0, как раньше. */
async function chainLatency(sr) {
    var NL = typeof window !== 'undefined' && window.NodeLatency;
    if (!NL) return { voice: 0, rx: 0 };
    var r = await Promise.all([NL.compressor(sr), NL.shaper(sr, '4x'), NL.shaper(sr, '2x')]);
    var comp = r[0] || 0, ws4 = r[1] || 0, ws2 = r[2] || 0;
    return { voice: comp + ws4 + comp + ws2, rx: comp + ws2 };
}

async function render(voice, opts) {
    opts = opts || {};
    var params = normalize(opts.params);
    var sr = voice.sampleRate;
    var L = await chainLatency(sr);
    var tl = timeline({ params: params, duration: voice.duration, extend: opts.extend !== false, sampleRate: sr });
    var nCh = opts.channels === 1 || opts.channels === 2 ? opts.channels : (plan(params, sr).stereo ? 2 : 1);
    var total = Math.round(tl.total * sr);
    var win = Math.round(num(opts.windowSec, WINDOW_S) * sr);
    var pre = Math.round(prerollSec(params) * sr);
    var OAC = (typeof OfflineAudioContext !== 'undefined') ? OfflineAudioContext : window.webkitOfflineAudioContext;
    for (var s = 0; s < total; s += win) {
        if (opts.signal && opts.signal.aborted) { var ab = new Error('cancelled'); ab.name = 'AbortError'; throw ab; }
        var e = Math.min(total, s + win);
        // On a render-quantum boundary of the WHOLE timeline: Chromium's
        // DynamicsCompressor updates its gain in 32-frame divisions counted from
        // the context's first frame, so a window starting off that grid compresses
        // on a shifted grid — measured −22 dB of difference against a single
        // render (2026-10-08), −88 dB once aligned.
        var from = Math.max(0, Math.floor((s - pre) / 128) * 128);
        var frames = e - from + L.voice;                 // окно читается на задержку цепочки позже
        var ctx = new OAC(nCh, frames, sr);
        var eng = build(ctx, params, { trimDb: opts.trimDb });
        if (opts.onGraph) opts.onGraph(eng, ctx);     // tests and benches reach into the nodes
        eng.output.connect(ctx.destination);
        layWindow(ctx, eng, tl, voice, from, frames, L.voice - L.rx);
        var out = await ctx.startRendering();
        var arrays = [], o0 = s - from + L.voice;
        for (var c = 0; c < nCh; c++) arrays.push(out.getChannelData(Math.min(c, out.numberOfChannels - 1)).subarray(o0, o0 + (e - s)));
        // The last window ends on a short fade: a hall or an echo still ringing at
        // the file's end was cut into a click (spectrogram, 2026-10-08).
        var fadeN = Math.round(END_FADE_S * sr), fadeFrom = total - fadeN;
        if (e > fadeFrom) {
            for (var c2 = 0; c2 < arrays.length; c2++) {
                var arr = arrays[c2];
                for (var fi = Math.max(fadeFrom, s); fi < e; fi++) arr[fi - s] *= Math.max(0, (total - fi) / fadeN);
            }
        }
        if (opts.onChunk) await opts.onChunk(arrays, { start: s, end: e, total: total });
        if (opts.onProgress) try { opts.onProgress(e / total); } catch (x) {}
        eng.dispose();
    }
    return { sampleRate: sr, channels: nCh, frames: total, timeline: tl };
}

window.MegaphoneEngine = {
    PRESETS: PRESETS,
    PRESET_ORDER: PRESET_ORDER,
    CTL_KEYS: CTL_KEYS,
    CTL_RANGE: CTL_RANGE,
    BUTTER_Q_DB: BUTTER_Q_DB,
    CEIL_LIMIT: CEIL_LIMIT,
    LIVE_TRIM_DB: LIVE_TRIM_DB,
    SPEECH_TARGET_DB: SPEECH_TARGET_DB,
    presetParams: presetParams,
    normalize: normalize,
    matchesPreset: matchesPreset,
    plan: plan,
    prerollSec: prerollSec,
    ringSec: ringSec,
    shaperCurve: shaperCurve,
    ceilingCurve: ceilingCurve,
    noiseLoop: noiseLoop,
    eventSamples: eventSamples,
    timeline: timeline,
    speechLevelDb: speechLevelDb,
    trimFor: trimFor,
    responseDb: responseDb,
    build: build,
    schedule: schedule,
    fire: fire,
    liveNoise: liveNoise,
    render: render
};
})();
